import styled from '@emotion/styled'
import { useEffect, useState } from 'react'

import { ApiSavedPortfolio, useGetByTenantPortfolioListQuery } from '@/api/rtk-api'
import {
  CheckboxWithLabel,
  Icon,
  LinkIcon,
  RadioTab,
  Space,
  Typography
} from '@/components/core-v2'
import { TENANT } from '@/const'

import { activeStatuses, inactiveStatuses } from '../../constants'
import { PortfolioSearchFilter } from '../../state/edit-portfolio-slice'
import { StyledDivider } from '../../styles'

export enum FilterFofPortfolioStatus {
  ALL = 'All',
  ACTIVE = 'Active',
  INACTIVE = 'Inactive'
}

export type PortfolioPanelHeaderProps = {
  canUserStartNew: boolean
  onSearch: (filter: PortfolioSearchFilter) => void
  onSelectNewPortfolio: () => void
  portfolios: ApiSavedPortfolio[]
  searchFilter: PortfolioSearchFilter
}

const StyledInput = styled.input`
  width: 100%;
  padding: 12px;
  margin: 0;
  border: none;
  background: var(--White95);
  font-size: 15px;
  outline: none;
  font-weight: normal;
  color: var(--Graphite);
`

export const PortfolioPanelHeader = ({
  canUserStartNew,
  onSearch,
  onSelectNewPortfolio,
  portfolios,
  searchFilter
}: PortfolioPanelHeaderProps) => {
  const { data: portfoliosTotal } = useGetByTenantPortfolioListQuery({
    tenant: TENANT,
    pageSize: 1000,
    wildcardString: searchFilter.searchTerm
  })
  type PortfoliosCount = {
    fofPortfoliosCount?: number
    modelPortfoliosCount?: number
    portfoliosActiveCount?: number
    portfoliosAllCount?: number
    portfoliosInactiveCount?: number
  }
  const [portfoliosCount, setPortfoliosCount] = useState<PortfoliosCount>({
    portfoliosAllCount: 0,
    modelPortfoliosCount: 0,
    fofPortfoliosCount: 0,
    portfoliosActiveCount: 0,
    portfoliosInactiveCount: 0
  })
  const onChangeActiveOrInactiveCheckboxes = (selectedState: FilterFofPortfolioStatus) => () => {
    const clonedFilter = { ...searchFilter }
    if (selectedState === FilterFofPortfolioStatus.ALL) {
      clonedFilter.showOnlyActivePortfolios = false
      clonedFilter.showOnlyInactivePortfolios = false
    } else if (selectedState === FilterFofPortfolioStatus.ACTIVE) {
      clonedFilter.showOnlyInactivePortfolios = false
      clonedFilter.showOnlyActivePortfolios = true
    } else if (selectedState === FilterFofPortfolioStatus.INACTIVE) {
      clonedFilter.showOnlyActivePortfolios = false
      clonedFilter.showOnlyInactivePortfolios = true
    }
    onSearch(clonedFilter)
  }

  const onSearchText = (searchText: string) => {
    const clonedFilter = { ...searchFilter }
    clonedFilter.searchTerm = searchText
    onSearch(clonedFilter)
  }

  const onShowFofOnlyChange = (checked: boolean) => {
    const clonedFilter = { ...searchFilter }
    clonedFilter.showFofPortfolios = checked
    onSearch(clonedFilter)
  }

  const onShowNewModelOnlyChange = (checked: boolean) => {
    const clonedFilter = { ...searchFilter }
    clonedFilter.showModelPortfolios = checked
    onSearch(clonedFilter)
  }

  // Calculate counts when portfolios data changes
  useEffect(() => {
    const data = portfoliosTotal?.data || []

    // Separate portfolios by type
    const fofPortfolios = []
    const modelPortfolios = []

    for (const portfolio of data) {
      if ((portfolio.type as string) === 'FundOfFunds') {
        fofPortfolios.push(portfolio)
      } else {
        modelPortfolios.push(portfolio)
      }
    }

    // Apply search filter to determine visible portfolios
    const combinedPortfolios = [
      ...(searchFilter.showFofPortfolios ? fofPortfolios : []),
      ...(searchFilter.showModelPortfolios ? modelPortfolios : [])
    ]

    // Count active/inactive statuses
    let portfoliosActiveCount = 0
    let portfoliosInactiveCount = 0

    for (const portfolio of combinedPortfolios) {
      if (activeStatuses.includes(portfolio.status)) {
        portfoliosActiveCount++
      } else if (inactiveStatuses.includes(portfolio.status)) {
        portfoliosInactiveCount++
      }
    }

    setPortfoliosCount({
      modelPortfoliosCount: modelPortfolios.length,
      fofPortfoliosCount: fofPortfolios.length,
      portfoliosAllCount: combinedPortfolios.length,
      portfoliosActiveCount,
      portfoliosInactiveCount
    })
  }, [portfoliosTotal, searchFilter.showFofPortfolios, searchFilter.showModelPortfolios])

  return (
    <>
      <Space mt={6}>
        <StyledInput type="text" onChange={e => onSearchText(e.target.value)} />
      </Space>
      <Space isflex justifyContent="space-between" py={5}>
        <Typography fontSize="15px" fontWeight="800" letterSpacing="0px" variant="b3">
          {portfolios.length} found
        </Typography>
        {canUserStartNew && (
          <LinkIcon
            disabled={false}
            icon={<Icon name="circleAdd" size="default" />}
            iconPosition="left"
            text="Add a new portfolio"
            onClick={() => onSelectNewPortfolio()}
          />
        )}
      </Space>
      <Space isflex justifyContent="flex-start" mb={5}>
        <Space mr={4}>
          <CheckboxWithLabel
            checked={searchFilter.showModelPortfolios}
            onChange={onShowNewModelOnlyChange}
          >
            Model portfolios ({portfoliosCount.modelPortfoliosCount})
          </CheckboxWithLabel>
        </Space>
        <CheckboxWithLabel checked={searchFilter.showFofPortfolios} onChange={onShowFofOnlyChange}>
          Fund of funds portfolios ({portfoliosCount.fofPortfoliosCount})
        </CheckboxWithLabel>
      </Space>
      <StyledDivider />
      <Space isflex justifyContent="flex-start" pt={5}>
        <Space mr={4}>
          <RadioTab
            isSelected={
              !searchFilter.showOnlyActivePortfolios && !searchFilter.showOnlyInactivePortfolios
            }
            onClick={onChangeActiveOrInactiveCheckboxes(FilterFofPortfolioStatus.ALL)}
          >
            All ({portfoliosCount.portfoliosAllCount})
          </RadioTab>
        </Space>
        <Space mr={4}>
          <RadioTab
            isSelected={searchFilter.showOnlyActivePortfolios}
            onClick={onChangeActiveOrInactiveCheckboxes(FilterFofPortfolioStatus.ACTIVE)}
          >
            Active ({portfoliosCount.portfoliosActiveCount})
          </RadioTab>
        </Space>
        <Space mr={4}>
          <RadioTab
            isSelected={
              !searchFilter.showOnlyActivePortfolios && searchFilter.showOnlyInactivePortfolios
            }
            onClick={onChangeActiveOrInactiveCheckboxes(FilterFofPortfolioStatus.INACTIVE)}
          >
            Inactive ({portfoliosCount.portfoliosInactiveCount})
          </RadioTab>
        </Space>
      </Space>
    </>
  )
}
